import { useRef, useLayoutEffect, useState, useCallback, SetStateAction, Dispatch } from 'react';
import ResizeObserver from 'resize-observer-polyfill';

/**
 * The Resize Observer API provides a mechanism by which code can monitor
 * an element for changes to its size, with notifications being delivered
 * to the observer each time the size changes.
 */
export function useResizeObserver(): [Dispatch<SetStateAction<Element | null>>, ResizeObserverEntry | null] {
  const [observerEntry, setObserverEntry] = useState<ResizeObserverEntry | null>(null);
  const [node, setNode] = useState<Element | null>(null);
  const observer = useRef<ResizeObserver>();

  const disconnect = useCallback(() => observer.current?.disconnect(), []);

  const observe = useCallback(() => {
    observer.current = new ResizeObserver(([entry]: ResizeObserverEntry[]) => setObserverEntry(entry));

    if (node) {
      observer.current.observe(node);
    }
  }, [node]);

  useLayoutEffect(() => {
    observe();
    return () => disconnect();
  }, [disconnect, observe]);

  return [setNode, observerEntry];
}
